import './FilterPopUp.scss';
import { useCallback, useRef, PropsWithChildren, useEffect, useState } from 'react';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import ValidationGroup from 'devextreme-react/validation-group';
import { useScreenSize } from '../../../utils/media-query';
import { Button } from 'devextreme-react';
import Form, { Item, Label } from 'devextreme-react/form';
import DropDownBox from 'devextreme-react/drop-down-box';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { LIS_States } from '../../../utils/systemTypes';
import { FilteredOrders } from '../../../types/types.orders';
import { useApiOrders } from '../../../api/api.orders';
import { setFilteredOrders, setFilteresToOrders, setFiltersToOrdersText, setPanelVisible } from '../../../contexts/slices/orders';
import { ConvertLocalDate } from '../../../utils/functions';
import { useFilterStudiesDropDown } from './hooks/useFilterStudiesDropDown';
import { FilterStudiesDataGrid } from './components/FilterStudiesDataGrid';
import { shallowEqual } from 'react-redux';

type PopupProps = {
  visible: boolean;
  setVisible: () => void;
};
const today = new Date().toISOString();
const FieldsToFilters: FilteredOrders = {
  Fechas: [today, today],
};

export const FilterPopup = ({ visible, setVisible }: PropsWithChildren<PopupProps>) => {
  const { isXSmall } = useScreenSize();
  const [loading, setLoading] = useState(false);
  const width = isXSmall ? 360 : 600;
  const frmRef = useRef<Form>(null);
  const refDropDownStudies = useRef<DropDownBox>(null);
  const LstEstudios = useAppSelector((x) => x.configuration.Data?.Estudios);
  const LstAreas = useAppSelector((x) => x.configuration.Data?.Areas);
  const LstServicios = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysA_Servicios')[0].Data;
  const LstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0].Data;
  const CurrentFilters = useAppSelector((x) => x.globalOrders.FiltersToOrders);
  const dispatch = useAppDispatch();
  const { GetOrdersByFilter } = useApiOrders();
  const Prm_Ord_ViewOtherLocation = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_ViewOtherLocation);
  const ModuleLocation: boolean = JSON.parse(useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ModuleLocation')[0].Cgn_Resultado, shallowEqual) || 'false');

  // Estado para el dropdown de estudios
  const [selectedStudios, setSelectedStudios] = useState<number[]>([]);

  const previousEstados = useRef<number[]>([]);

  // Custom hook para el DropDownBox de estudios
  const handleStudiosChange = useCallback((estudiosIds: number[]) => {
    setSelectedStudios(estudiosIds);
  }, []);

  const filterStudiesDropDown = useFilterStudiesDropDown(handleStudiosChange);

  // Función para generar el texto descriptivo de los filtros
  const generateFiltersText = (filters: FilteredOrders): string[] => {
    const filtersText: string[] = [];
    const today = new Date();
    const todayConverted = ConvertLocalDate(today);

    // Fechas (solo si no son del día actual)
    if (filters.Fechas && filters.Fechas.length === 2) {
      const fechaInicio = filters.Fechas[0];
      const fechaFin = filters.Fechas[1];

      // Convertir las fechas usando la misma función que se usa en el sistema
      const fechaInicioConverted = typeof fechaInicio === 'string' ? fechaInicio : ConvertLocalDate(fechaInicio);
      const fechaFinConverted = typeof fechaFin === 'string' ? fechaFin : ConvertLocalDate(fechaFin);

      // Comparar solo las fechas (sin horas) usando la función del sistema
      const fechaInicioDate = fechaInicioConverted.split('T')[0];
      const fechaFinDate = fechaFinConverted.split('T')[0];
      const todayDate = todayConverted.split('T')[0];

      // Solo agregar si no son fechas del día actual
      if (fechaInicioDate !== todayDate || fechaFinDate !== todayDate) {
        const fechaInicioFormat = new Date(fechaInicio).toLocaleDateString();
        const fechaFinFormat = new Date(fechaFin).toLocaleDateString();
        filtersText.push(`Fechas: ${fechaInicioFormat} - ${fechaFinFormat}`);
      }
    }

    // Orden
    if (filters.Orden) {
      filtersText.push(`Orden: ${filters.Orden}`);
    }

    // Paciente
    if (filters.Paciente) {
      filtersText.push(`Paciente: ${filters.Paciente}`);
    }

    // Áreas
    if (filters.Areas && filters.Areas.length > 0) {
      const areasNames = filters.Areas.map((areaId) => LstAreas?.find((area) => area.Aa_Id === areaId)?.Aa_Nombre || `ID: ${areaId}`).join(', ');
      filtersText.push(`Áreas: ${areasNames}`);
    }

    // Estudios
    if (filters.Estudios && filters.Estudios.length > 0) {
      const estudiosNames = filters.Estudios.map((estudioId) => LstEstudios?.find((estudio) => estudio.Es_Id === estudioId)?.Es_Nombre || `ID: ${estudioId}`).join(', ');
      filtersText.push(`Estudios: ${estudiosNames}`);
    }

    // Servicios
    if (filters.Servicios && filters.Servicios.length > 0) {
      const serviciosNames = filters.Servicios.map((servicioId) => LstServicios?.find((servicio) => servicio.Se_Id === servicioId)?.Se_Nombre || `ID: ${servicioId}`).join(', ');
      filtersText.push(`Servicios: ${serviciosNames}`);
    }

    // Estados
    if (filters.Estados && filters.Estados.length > 0) {
      const estadosNames = filters.Estados.map((estadoId) => LIS_States.find((estado) => estado.Value === estadoId)?.State || `ID: ${estadoId}`).join(', ');
      filtersText.push(`Estados: ${estadosNames}`);
    }

    // Sedes
    if (filters.Sedes && filters.Sedes.length > 0) {
      const sedesNames = filters.Sedes.map((sedeId) => LstSedes?.find((sede) => sede.Sd_Id === sedeId)?.Sd_Nombre || `ID: ${sedeId}`).join(', ');
      filtersText.push(`Sedes: ${sedesNames}`);
    }
    

    return filtersText;
  };

  const validationGroup = useRef<ValidationGroup>(null);
  useEffect(() => {
    if (visible) {
      validationGroup.current?.instance.reset();
    }
  }, [visible]);

  const close = () => {
    validationGroup.current?.instance.reset();
    setVisible();
  };

  const onCancelClick = useCallback(() => {
    close();
  }, [close, validationGroup]);

  const handleConfirm = useCallback(() => {
    if (frmRef.current?.instance) {
      // Obtener todos los valores del formulario
      const fechas = frmRef.current.instance.getEditor('Fechas')?.option('value');
      const orden = frmRef.current.instance.getEditor('Orden')?.option('value');
      const paciente = frmRef.current.instance.getEditor('Paciente')?.option('value');
      const areas = frmRef.current.instance.getEditor('Areas')?.option('value');
      const servicios = frmRef.current.instance.getEditor('Servicios')?.option('value');
      const estados = frmRef.current.instance.getEditor('Estados')?.option('value');
      const sedes = frmRef.current.instance.getEditor('Sedes')?.option('value');

      // Validación: Si no hay fechas, debe haber al menos Orden o Paciente
      const hasFechas = fechas && Array.isArray(fechas) && fechas.length === 2 && fechas[0] && fechas[1];
      const hasOrden = orden && orden.trim() !== '';
      const hasPaciente = paciente && paciente.trim() !== '';

      if (!hasFechas && !hasOrden && !hasPaciente) {
        // Mostrar error de validación
        const fechasEditor = frmRef.current.instance.getEditor('Fechas');
        const ordenEditor = frmRef.current.instance.getEditor('Orden');
        const pacienteEditor = frmRef.current.instance.getEditor('Paciente');

        if (fechasEditor) fechasEditor.option('isValid', false);
        if (ordenEditor) ordenEditor.option('isValid', false);
        if (pacienteEditor) pacienteEditor.option('isValid', false);

        return;
      }

      setLoading(true);

      const fechasConvertidas = hasFechas ? fechas.map((fecha: Date) => ConvertLocalDate(new Date(fecha))) : [];
      const updatedFieldsToFilters: FilteredOrders = {
        Fechas: fechasConvertidas,
        Orden: orden || '',
        Paciente: paciente || '',
        Areas: areas || [],
        Estudios: selectedStudios || [], // Usar el estado local en lugar del editor del formulario
        Servicios: servicios || [],
        Estados: estados || [],
        Sedes: sedes || [],
        Redirect: false, // No redirigir automáticamente desde el popup
      };

      GetOrdersByFilter(updatedFieldsToFilters)
        .then((res) => {
          dispatch(setFilteredOrders(res));
          dispatch(setFilteresToOrders(updatedFieldsToFilters));

          // Generar y guardar el texto de los filtros
          const filtersText = generateFiltersText(updatedFieldsToFilters);
          dispatch(setFiltersToOrdersText(filtersText));

          dispatch(setPanelVisible(true));
          close();
        })
        .catch((error) => {
          console.error('Error al obtener órdenes filtradas:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedStudios, GetOrdersByFilter, dispatch, close, generateFiltersText]);

  useEffect(() => {
    if (!visible) {
      frmRef.current?.instance.updateData(CurrentFilters);
    } else {
      frmRef.current?.instance.beginUpdate();
    }
  }, [visible, CurrentFilters]);

  // Efecto para actualizar automáticamente las órdenes cuando cambien los filtros desde el TagBox
  useEffect(() => {
    const currentEstados = CurrentFilters?.Estados || [];
    if (CurrentFilters && !visible && CurrentFilters.Fechas && CurrentFilters.Fechas.length > 0 && previousEstados.current.length >= 0 && JSON.stringify(currentEstados) !== JSON.stringify(previousEstados.current)) {
      setLoading(true);

      GetOrdersByFilter(CurrentFilters)
        .then((res) => {
          dispatch(setFilteredOrders(res));

          // Actualizar también el texto de los filtros
          const filtersText = generateFiltersText(CurrentFilters);
          dispatch(setFiltersToOrdersText(filtersText));

          dispatch(setPanelVisible(true));
        })
        .catch((error) => {
          console.error('Error al actualizar órdenes filtradas:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }

    // Actualizar la referencia anterior
    previousEstados.current = [...currentEstados];
  }, [CurrentFilters?.Estados, visible, GetOrdersByFilter, dispatch, generateFiltersText]); // Solo escuchar cambios en Estados

  const handleCancel = useCallback(() => {
    frmRef.current?.instance.resetValues();
    setVisible();
  }, [FieldsToFilters]);

  return (
    <Popup title='Filtrar órdenes' visible={visible} showCloseButton={true} onHiding={onCancelClick} fullScreen={isXSmall} width={width} wrapperAttr={{ class: `form-popup` }} height='auto'>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleConfirm();
        }}>
        <Form ref={frmRef} formData={FieldsToFilters}>
          <Item dataField='Fechas' editorType='dxDateRangeBox' editorOptions={{ displayFormat: 'shortdate', showClearButton: true }}>
            <Label text='Fecha' />
          </Item>
          <Item dataField='Orden'>
            <Label text='Orden' />
          </Item>
          <Item dataField='Paciente'>
            <Label text='Paciente' />
          </Item>
          <Item dataField='Areas' editorOptions={{ items: LstAreas, showSelectionControls: true, showClearButton: true, showDataBeforeSearch: true, minSearchLength: 2, searchTimeout: 200, searchEnabled: true, displayExpr: 'Aa_Nombre', valueExpr: 'Aa_Id' }} editorType='dxTagBox'>
            <Label text='Área' />
          </Item>
          <Item
            dataField='Estudios'
            cssClass='full-width-item'
            render={() => (
              <DropDownBox
                value={filterStudiesDropDown.studiesDropDownValue}
                opened={filterStudiesDropDown.isStudiesDropDownOpened}
                valueExpr='Es_Id'
                deferRendering={false}
                displayExpr={filterStudiesDropDown.studiesDisplayExpr}
                placeholder='Seleccionar estudio...'
                showClearButton={true}
                stylingMode='outlined'
                className='comboboxItems'
                focusStateEnabled={true}
                width='100%'
                ref={refDropDownStudies}
                dataSource={filterStudiesDropDown.studiesDataSource}
                onValueChanged={filterStudiesDropDown.syncStudiesSelection}
                onOptionChanged={filterStudiesDropDown.onStudiesDropDownOpened}
                onKeyDown={filterStudiesDropDown.onStudiesKeyDown}
                contentRender={() => (
                  <FilterStudiesDataGrid
                    studiesDataSource={filterStudiesDropDown.studiesDataSource}
                    studiesDropDownValue={filterStudiesDropDown.studiesDropDownValue}
                    onStudiesSelectionChanged={filterStudiesDropDown.onStudiesSelectionChanged}
                    onStudiesRowDblClick={filterStudiesDropDown.onStudiesRowDblClick}
                    studiesSearchValue={filterStudiesDropDown.studiesSearchValue}
                    onStudiesSearchValueChanged={filterStudiesDropDown.onStudiesSearchValueChanged}
                    onStudiesKeyDown={filterStudiesDropDown.onStudiesKeyDown}
                    setIsStudiesDropDownOpened={filterStudiesDropDown.setIsStudiesDropDownOpened}
                    refDropDownStudies={refDropDownStudies}
                  />
                )}
                dropDownOptions={{
                  width: isXSmall ? '100vw' : 700,
                  height: isXSmall ? '100vh' : 500,
                  showTitle: false,
                  shading: false,
                  closeOnOutsideClick: !isXSmall,
                }}
              />
            )}>
            <Label text='Estudio' />
          </Item>
          <Item dataField='Servicios' visible={LstServicios && LstServicios.length > 0} editorOptions={{ items: LstServicios, showSelectionControls: true, displayExpr: 'Se_Nombre', valueExpr: 'Se_Id' }} editorType='dxTagBox'>
            <Label text='Servicio' />
          </Item>
          <Item dataField='Sedes' visible={LstSedes && LstSedes.length > 0 && ModuleLocation && Prm_Ord_ViewOtherLocation} editorOptions={{ items: LstSedes, showSelectionControls: true, displayExpr: 'Sd_Nombre', valueExpr: 'Sd_Id' }} editorType='dxTagBox'>
            <Label text='Sede' />
          </Item>
          <Item dataField='Estados' editorOptions={{ items: LIS_States, showSelectionControls: true, displayExpr: 'State', valueExpr: 'Value' }} editorType='dxTagBox'>
            <Label text='Estado' />
          </Item>
        </Form>
        <button type='submit' style={{ display: 'none' }} aria-hidden />
      </form>
      <ToolbarItem toolbar='bottom' location='after'>
        <div className={`form-popup-buttons-container`}>
          <Button text='Cancelar' stylingMode='contained' onClick={handleCancel} />
          <Button text='Confirmar' disabled={loading} stylingMode='contained' type='default' onClick={handleConfirm} />
        </div>
      </ToolbarItem>
    </Popup>
  );
};
